import { ArrowRight } from "lucide-react";
import { cn } from "../../../lib/utils";
import { lpConfig } from "../lpConfig";
import { useFadeIn } from "../useFadeIn";

const problems = lpConfig.problem.cards.map(card => ({
  ...card,
  image: `${process.env.PUBLIC_URL}/images/lp/${card.image}`,
}));

/**
 * ランディングページの課題提起セクション
 * SES営業における3つの主要課題とBefore/Afterの比較を表示する
 * @return {JSX.Element} 課題提起セクションコンポーネント
 */
export function LpProblem() {
  const fade = useFadeIn();

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div
        ref={fade.ref}
        className={cn(
          "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 lp-fade-in-up",
          fade.isVisible && "lp-visible",
        )}
      >
        <div className="text-center mb-16">
          <span className="text-xs font-bold tracking-widest text-[#ff8d00] uppercase mb-4 block">
            {lpConfig.problem.sectionLabel}
          </span>
          <h2 className="text-4xl font-bold text-[#0F1649]">
            {lpConfig.problem.heading}
          </h2>
        </div>

        {/* Problem cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {problems.map(problem => (
            <div
              key={problem.title}
              className="rounded-2xl border border-gray-100 bg-gray-50/50 hover:shadow-lg hover:border-gray-200 transition-all overflow-hidden"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={problem.image}
                  alt={problem.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-[#0F1649] mb-3">
                  {problem.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {problem.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Before / After comparison */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 items-center">
          {/* Before */}
          <div className="bg-red-50/50 rounded-2xl p-8 text-center">
            <span className="text-xs font-bold tracking-widest text-red-400 uppercase mb-4 block">
              {lpConfig.problem.before.label}
            </span>
            <p className="text-4xl font-bold text-red-500 mb-2">
              {lpConfig.problem.before.value}
            </p>
            <p className="text-gray-500">{lpConfig.problem.before.caption}</p>
          </div>

          {/* Arrow */}
          <div className="flex justify-center">
            <ArrowRight className="w-8 h-8 text-[#30BF78]" />
          </div>

          {/* After */}
          <div className="bg-[#30BF78]/10 rounded-2xl p-8 text-center relative">
            <span className="text-xs font-bold tracking-widest text-[#30BF78] uppercase mb-4 block">
              {lpConfig.problem.after.label}
            </span>
            <p className="text-4xl font-bold text-[#30BF78] mb-2">
              {lpConfig.problem.after.value}
            </p>
            <p className="text-gray-500">{lpConfig.problem.after.caption}</p>
            <span className="absolute -top-3 -right-3 bg-[#30BF78] text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
              {lpConfig.problem.after.badge}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
